/**
 * Authentication Guard Hook for Checkout Actions
 *
 * This hook provides authentication checking for checkout/buy-now actions.
 * It follows the same pattern as useWishList in the main app.
 *
 * IMPORTANT: Guest users CAN add to cart without authentication.
 * Authentication is only required for actions that proceed to checkout.
 *
 * Usage in components:
 * ```tsx
 * const { isAuthenticated, requireAuthentication } = useAuthenticationGuard();
 *
 * const handleBuyNow = () => {
 *   if (requireAuthentication()) {
 *     // User is authenticated, proceed to checkout
 *     navigateToCheckout();
 *   }
 *   // If not authenticated, login modal will be shown automatically
 * };
 *
 * const handleAddToCart = () => {
 *   // NO authentication check needed — guests can add to cart
 *   addToCart();
 * };
 * ```
 *
 * @module @kidslabs/module-quote/hooks/use-authentication-guard
 */

import { useCallback } from 'react';

/**
 * Configuration for authentication guard
 */
export interface AuthenticationGuardConfig {
    /**
     * Check if user is authenticated
     */
    isAuthenticated: boolean;

    /**
     * Whether the authentication status is still being loaded
     * When true, requireAuthentication() will return false without opening modal
     */
    isLoading?: boolean;

    /**
     * Function to open login modal/view
     */
    openLoginModal: () => void;
}

/**
 * Return type for useAuthenticationGuard hook
 */
export interface AuthenticationGuardResult {
    /**
     * Whether the user is currently authenticated
     */
    isAuthenticated: boolean;

    /**
     * Function that checks authentication and opens login modal if not authenticated
     * Returns true if authenticated, false if not (and modal was opened)
     */
    requireAuthentication: () => boolean;
}

/**
 * Hook to guard cart actions with authentication check
 *
 * This hook is designed to be used with dependency injection pattern.
 * The main app will provide authentication state and modal opener.
 *
 * @param config - Configuration with auth state and modal opener
 * @returns Authentication guard utilities
 */
export const useAuthenticationGuard = (config: AuthenticationGuardConfig): AuthenticationGuardResult => {
    const { isAuthenticated, isLoading = false, openLoginModal } = config;

    const requireAuthentication = useCallback((): boolean => {
        // If still loading session, don't do anything (no animation, no modal)
        if (isLoading) {
            return false;
        }

        if (isAuthenticated) {
            return true;
        }

        openLoginModal();
        return false;
    }, [isAuthenticated, isLoading, openLoginModal]);

    return {
        isAuthenticated,
        requireAuthentication,
    };
};
